// Account plan + weekly AI-action usage. Reads waggle-api through the
// /api/account/* proxy routes (Stripe sandbox). The numbers here only drive
// the UI: the server gates generation (402 ai_actions_exhausted).
import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";
import { apiRequest } from "@/lib/queryClient";

export type PlanId = "free" | "pro";

export const FREE_WEEKLY_LIMIT = 10;
// Placeholder — pricing is still an open question.
export const PRO_PRICE_MONTHLY = 9;

export interface PlanFeatureSet {
  id: PlanId;
  name: string;
  price: string;
  cadence: string;
  features: string[];
}

export const PLANS: PlanFeatureSet[] = [
  {
    id: "free",
    name: "Free",
    price: "$0",
    cadence: "forever",
    features: [
      `Create or regenerate up to ${FREE_WEEKLY_LIMIT} questions a week`,
      "Every question type and export format",
    ],
  },
  {
    id: "pro",
    name: "Pro",
    price: `$${PRO_PRICE_MONTHLY}`,
    cadence: "per month",
    features: ["Unlimited question generation and regeneration", "Everything in Free"],
  },
];

export interface Invoice {
  id: string;
  date: string;
  amount: number;
  currency: string;
  description: string;
  status: string | null;
  hostedUrl: string | null;
}

export interface PlanState {
  plan: PlanId;
  /** Stripe subscription status; null when the user has never subscribed. */
  status: string | null;
  currentPeriodEnd: Date | null;
  cancelAtPeriodEnd: boolean;
  /** AI actions used since the start of the current week. */
  used: number;
  limit: number;
  remaining: number;
  isUnlimited: boolean;
  /** Free plan with no AI actions left this week (false while loading). */
  isOutOfActions: boolean;
  resetsAt: Date;
  /** Whether the user can start a Stripe checkout. */
  billingEnabled: boolean;
  isLoading: boolean;
}

interface PlanResponse {
  plan: PlanId;
  status: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
}

interface UsageResponse {
  used: number;
  limit: number;
  resetsAt: string;
}

/** Monday 00:00 local time of the week after `date`. */
export const nextWeeklyReset = (date = new Date()) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7) + 7);
  return d;
};

const PLAN_KEY = ["/api/account/plan"];
export const USAGE_KEY = ["/api/account/usage"];
const INVOICES_KEY = ["/api/account/invoices"];

export function usePlan(): PlanState {
  const { user } = useAuth();
  const enabled = !!user;
  const planQuery = useQuery<PlanResponse>({ queryKey: PLAN_KEY, enabled, staleTime: 30_000 });
  const usageQuery = useQuery<UsageResponse>({ queryKey: USAGE_KEY, enabled, staleTime: 30_000, refetchOnWindowFocus: true });

  // Free defaults while loading, signed out, or if the API is unreachable.
  const plan = planQuery.data?.plan ?? "free";
  const used = usageQuery.data?.used ?? 0;
  const limit = usageQuery.data?.limit ?? FREE_WEEKLY_LIMIT;
  return {
    plan,
    status: planQuery.data?.status ?? null,
    currentPeriodEnd: planQuery.data?.currentPeriodEnd ? new Date(planQuery.data.currentPeriodEnd) : null,
    cancelAtPeriodEnd: planQuery.data?.cancelAtPeriodEnd ?? false,
    used,
    limit,
    remaining: Math.max(0, limit - used),
    isUnlimited: plan === "pro",
    isOutOfActions: enabled && !planQuery.isLoading && !usageQuery.isLoading && plan === "free" && limit - used <= 0,
    resetsAt: usageQuery.data?.resetsAt ? new Date(usageQuery.data.resetsAt) : nextWeeklyReset(),
    billingEnabled: enabled && plan === "free",
    isLoading: enabled && (planQuery.isLoading || usageQuery.isLoading),
  };
}

/** Payment history, fetched live from Stripe. Empty on any error (e.g. billing not configured). */
export function useInvoices(): Invoice[] {
  const { user } = useAuth();
  const { data } = useQuery<Invoice[]>({ queryKey: INVOICES_KEY, enabled: !!user, staleTime: 30_000 });
  return data ?? [];
}

/** Starts Stripe Checkout and sends the browser to the Stripe-hosted page. */
export function useUpgrade() {
  return useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/billing/checkout-session");
      const { url } = (await res.json()) as { url: string };
      return url;
    },
    onSuccess: (url) => {
      window.location.assign(url);
    },
  });
}

/**
 * Dev options only: put the signed-in user back to Free, never subscribed
 * (Stripe customer deleted server-side), then refresh everything the Billing
 * page reads. Needs DEV_TOOLS_ENABLED on both servers.
 */
export function useResetSubscription() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (opts: { resetUsage: boolean }) => {
      await apiRequest("POST", "/api/dev/billing/reset", opts);
    },
    onSuccess: async () => {
      await Promise.all(
        [PLAN_KEY, USAGE_KEY, INVOICES_KEY].map((queryKey) => queryClient.invalidateQueries({ queryKey })),
      );
    },
  });
}

/** Dev options only: set how many AI actions are left this week (0 = exhausted). */
export function useSetAvailableActions() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (remaining: number) => {
      await apiRequest("POST", "/api/dev/usage/available", { remaining });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: USAGE_KEY }),
  });
}

/**
 * After Stripe redirects back with ?checkout=success the webhook may not have
 * landed yet, so refetch the plan a few times until it flips to Pro, then drop
 * the query param.
 */
export function useCheckoutReturn() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const result = params.get("checkout");
    if (!result || !user) return;

    params.delete("checkout");
    const rest = params.toString();
    window.history.replaceState(null, "", window.location.pathname + (rest ? `?${rest}` : ""));
    if (result !== "success") return;

    let attempts = 0;
    let timer: ReturnType<typeof setTimeout>;
    const poll = async () => {
      await queryClient.invalidateQueries({ queryKey: PLAN_KEY });
      await queryClient.invalidateQueries({ queryKey: INVOICES_KEY });
      const plan = queryClient.getQueryData<PlanResponse>(PLAN_KEY);
      if (plan?.plan !== "pro" && ++attempts < 10) timer = setTimeout(poll, 1500);
    };
    void poll();
    return () => clearTimeout(timer);
  }, [user, queryClient]);
}

export const formatShortDate = (
  date: Date | string,
  opts: Intl.DateTimeFormatOptions = { weekday: "short", month: "short", day: "numeric" },
) => new Date(date).toLocaleDateString("en-US", opts);
